import React from "react";
import {
  useFps,
  useMaxFrameTime,
  useTriangles,
  useDrawCalls,
  useGeometries,
  useTextures,
  useMemoryUsed,
} from "../../stores/metricsStore";
import { usePhysicsStore } from "../../stores/physicsStore";

const PerformanceItem = ({ label, value, color, width = "auto" }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
    <span style={{ color }}>{label}:</span>
    <span
      style={{
        fontWeight: "bold",
        width,
        textAlign: "right",
        display: "inline-block",
      }}
    >
      {value}
    </span>
  </div>
);

const FpsItem = () => {
  const fps = useFps();
  return <PerformanceItem label="FPS" value={fps} color="#4ade80" width="24px" />;
};

const LagItem = () => {
  const maxFrameTime = useMaxFrameTime();
  return <PerformanceItem label="Lag" value={`${maxFrameTime}ms`} color="#f87171" width="32px" />;
};

const TrianglesItem = () => {
  const triangles = useTriangles();
  return <PerformanceItem label="Tris" value={triangles} color="#60a5fa" width="48px" />;
};

const DrawCallsItem = () => {
  const drawCalls = useDrawCalls();
  return <PerformanceItem label="Draws" value={drawCalls} color="#f59e0b" width="24px" />;
};

const GeometriesItem = () => {
  const geometries = useGeometries();
  return <PerformanceItem label="Geom" value={geometries} color="#ec4899" width="24px" />;
};

const TexturesItem = () => {
  const textures = useTextures();
  return <PerformanceItem label="Tex" value={textures} color="#8b5cf6" width="24px" />;
};

const MemoryItem = () => {
  const memoryUsed = useMemoryUsed();
  if (memoryUsed <= 0) return null;
  return <PerformanceItem label="Mem" value={`${memoryUsed}MB`} color="#ef4444" width="40px" />;
};

const PhysicsStatus = () => {
  const engine = usePhysicsStore((s) => s.engine);
  const mapped = usePhysicsStore((s) => s.mappedBodies);
  const total = usePhysicsStore((s) => s.totalBodies);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ color: engine === "mujoco" ? "#4ade80" : "#f87171" }}>Engine: {engine}</span>
      {total > 0 && (
        <span style={{ color: "#60a5fa" }}>
          Mapped: {mapped}/{total}
        </span>
      )}
    </div>
  );
};

const PerformancePanel = ({ position = {} }) => {
  // Check if we should show the panel at all
  const fps = useFps();
  const triangles = useTriangles();

  // Don't show panel if no metrics are available yet
  if (fps === 0 && triangles === 0) return null;

  return (
    <div
      className="panel-container"
      style={{
        position: "fixed",
        ...position,
        background: "rgba(20, 20, 20, 0.85)",
        color: "white",
        padding: "8px 16px",
        borderRadius: 8,
        fontSize: 12,
        display: "flex",
        gap: 16,
        alignItems: "center",
        pointerEvents: "auto",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
      }}
    >
      <FpsItem />
      <LagItem />
      <TrianglesItem />
      <DrawCallsItem />
      <GeometriesItem />
      <TexturesItem />
      <MemoryItem />
      <PhysicsStatus />
    </div>
  );
};

export default PerformancePanel;
